import introJs from 'intro.js';
import { NCNode, GraphMode } from '../../../../src/commonTypes';
import { clearNonBlockingMessage, showNonBlockingMessage } from '../ui/uiUtils';
import { WINDOW_BREAKPOINTS } from '../constants';
import { disableChatbot, enableChatbot } from './tutorialEvents';
import { hideMiniTutorial, isMiniTutorialHidden } from './miniTutorial';

const tutorialButton = document.querySelector<HTMLButtonElement>('#tutorial')!;
if (tutorialButton) {
	tutorialButton.addEventListener('click', () => {
		if (globalThis.neuroCreate.graph) {
			globalThis.neuroCreate.graph.startTutorial();
		}
	});
}

export const deactivateTutorialButton = () => {
	if (tutorialButton.className === 'tutorialActive') {
		tutorialButton.classList.remove('tutorialActive');
	}
};

// Allows any element on page to be clicked (including SVG)
const enableClickthrough = () => {
	document.body.classList.add('enable-tutorial-clickthrough');
};

// Reverts to default Intro.js behavior, can only click on highlighted element
const disableClickthrough = () => {
	document.body.classList.remove('enable-tutorial-clickthrough');
};

export type IntroJs = ReturnType<typeof introJs>;

type StepExtra = {
	completeStep?: (target: HTMLOrSVGElement) => Promise<void>;
};

// Omit intro.js's boolean `dontShowAgain` so we can use GraphMode for per-board prefs.
type StartTutorialOptions = Omit<Partial<IntroJs['_options']>, 'dontShowAgain'> & {
	steps: Array<StepExtra>;
	dontShowAgain?: GraphMode;
};

let timeout: NodeJS.Timeout | undefined;
let tutorial: IntroJs | undefined;
let dontShowAgainMode: GraphMode | null = null;
let dontShowAgainChecked = false;
let persistedDontShowAgain = false;

const INTRO_JS_LAYER_SELECTORS = [
	'.introjs-overlay',
	'.introjs-helperLayer',
	'.introjs-tooltipReferenceLayer',
	'.introjs-disableInteraction',
	'.introjsFloatingElement',
];

/**
 * Intro.js fades its helper layer for 500ms when exiting. A follow-up tour can
 * start from the previous tour's onexit callback during that window, causing
 * Intro.js to reuse the stale helper layer and its old highlighted element.
 * Remove tour-owned DOM and classes synchronously at every boundary instead.
 */
const removeIntroJsArtifacts = (): void => {
	for (const selector of INTRO_JS_LAYER_SELECTORS) {
		document.querySelectorAll(selector).forEach((element) => element.remove());
	}

	document.querySelectorAll('.introjs-showElement, .introjs-relativePosition').forEach((element) => {
		for (const className of [...element.classList]) {
			if (className.startsWith('introjs-')) {
				element.classList.remove(className);
			}
		}
	});
};

const syncDontShowAgainCheckbox = (mode: GraphMode) => {
	const tooltip = document.querySelector('.introjs-tooltip');
	if (!tooltip) {
		return;
	}

	let label = tooltip.querySelector<HTMLLabelElement>('.introjs-dont-show-again');
	if (!label) {
		label = document.createElement('label');
		label.className = 'introjs-dont-show-again';
		const input = document.createElement('input');
		input.type = 'checkbox';
		input.addEventListener('change', () => {
			dontShowAgainChecked = input.checked;
		});
		label.append(input, document.createTextNode(" Don't show again"));
		const buttons = tooltip.querySelector('.introjs-tooltipbuttons');
		if (buttons) {
			tooltip.insertBefore(label, buttons);
		} else {
			tooltip.appendChild(label);
		}
	}

	const input = label.querySelector('input');
	if (input) {
		input.checked = dontShowAgainChecked;
	}
	label.dataset.tutorialMode = mode;
};

const finishDontShowAgainIfChecked = (mode?: GraphMode): void => {
	if (!mode || !dontShowAgainChecked) {
		return;
	}
	if (!persistedDontShowAgain) {
		persistedDontShowAgain = true;
		hideMiniTutorial(mode);
		globalThis.neuroCreate.graph?.setHintsShown();
	}
};

// options must include 'steps'
export const startTutorial = (
	options: StartTutorialOptions,
	handlers: {
		onexit?: () => void;
		oncomplete?: () => void;
		onafterchange?: (target: HTMLElement) => void;
		onbeforechange?: (target: HTMLElement) => boolean | Promise<boolean>;
	} = {}
) => {
	const { dontShowAgain, ...introOptions } = options;
	if (dontShowAgain && isMiniTutorialHidden(dontShowAgain)) {
		globalThis.neuroCreate.graph?.setHintsShown();
		return;
	}
	if (dontShowAgain) {
		if (dontShowAgainMode !== dontShowAgain) {
			dontShowAgainChecked = false;
			persistedDontShowAgain = false;
		}
		dontShowAgainMode = dontShowAgain;
	}

	disableChatbot();
	// Do not call exit() on a fresh Intro.js instance here. Exit is asynchronous
	// and its delayed helper-layer removal can corrupt the next tour.
	removeIntroJsArtifacts();
	tutorial = introJs().setOptions({
		showBullets: Boolean(options.steps && options.steps.length > 1),
		showButtons: true,
		keyboardNavigation: true,
		exitOnOverlayClick: false,
		disableInteraction: true,
		scrollToElement: false,
		...introOptions,
	});

	if (options.disableInteraction === false) {
		enableClickthrough();
	} else {
		disableClickthrough();
	}

	const { onexit, oncomplete, onafterchange, onbeforechange } = handlers;
	tutorial = tutorial.onexit(() => {
		finishDontShowAgainIfChecked(dontShowAgain);
		onexit?.();
	});
	if (oncomplete) {
		tutorial = tutorial.oncomplete(() => {
			finishDontShowAgainIfChecked(dontShowAgain);
			oncomplete();
		});
	}
	tutorial = tutorial.onafterchange((target) => {
		if (dontShowAgain) {
			syncDontShowAgainCheckbox(dontShowAgain);
		}
		if (onafterchange) {
			onafterchange(target);
		}
		if (globalThis.neuroCreate.automateTutorial) {
			// Clear timeout as it may have been set but not yet triggered by a previous step
			timeout && clearTimeout(timeout);
			const currentStepIndex = tutorial?.currentStep();
			if (currentStepIndex !== undefined) {
				const currentStep = options.steps[currentStepIndex];
				if (currentStep.completeStep) {
					currentStep.completeStep(target).then(() => {
						timeout = setTimeout(() => {
							tutorial?.nextStep();
						}, 1000);
					});
				}
			}
		}
	});
	tutorial = tutorial.onbeforechange((target) => {
		if (target) {
			const scrollOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' };
			console.log('scrollTo', target);
			if (true) {
				console.log('scrolling');
				// Scroll to target if it is not in view
				// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoViewIfNeeded#browser_compatibility
				// @ts-expect-error This is not a standard method, but it is available in many browsers
				target.scrollIntoViewIfNeeded
					? // @ts-expect-error This is not a standard method, but it is available in many browsers
						// eslint-disable-next-line @typescript-eslint/no-unsafe-call
						target.scrollIntoViewIfNeeded(scrollOptions)
					: target.scrollIntoView(scrollOptions);
			}
		}
		if (onbeforechange) {
			return onbeforechange(target);
		}
		return true;
	});
	tutorial.start();
};

export const isTutorialActive = (graphMode: GraphMode, templateId?: string) => {
	if (templateId) {
		return Boolean(
			globalThis.neuroCreate.graph && globalThis.neuroCreate.graph.graphMode === graphMode
		);
	} else {
		return Boolean(
			globalThis.neuroCreate.graph &&
				globalThis.neuroCreate.graph.graphMode === graphMode &&
				tutorialButton.className === 'tutorialActive'
		);
	}
};

export const exitTutorial = () => {
	const tutorialToExit = tutorial;

	// Detach first because Intro.js invokes onexit asynchronously. The callback
	// may start a follow-up tour and must be allowed to store that new instance.
	tutorial = undefined;
	if (timeout) {
		clearTimeout(timeout);
		timeout = undefined;
	}

	// Remove the visual state before asking Intro.js to run its callbacks. This
	// guarantees the board is immediately usable even if the instance reference
	// was lost by an older flow or Intro.js delays its own fade-out cleanup.
	removeIntroJsArtifacts();
	if (tutorialToExit) {
		void tutorialToExit.exit(true).catch((error: unknown) => {
			console.error('Unable to exit tutorial cleanly:', error);
			removeIntroJsArtifacts();
		});
	}
	clearNonBlockingMessage();
	console.log('Exiting tutorial');
	enableChatbot();
};

export const endTutorial = () => {
	removeIntroJsArtifacts();
	tutorialButton.classList.remove('tutorialActive');

	// Close expanded menu to ensure it will be expanded when tutorial is restarted
	closeExpandedMenuAfterTutorial(
		document.querySelector<HTMLButtonElement>('#local-menu .expand-menu')!,
		document.querySelector<HTMLButtonElement>('#local-menu .collapse-menu')!
	);
	console.log("Ending tutorial");
	enableChatbot();
	disableClickthrough();
};

export const updatePosition = () => {
	if (tutorial) {
		tutorial.refresh();
	}
};

export const addTextWithTypingEffect = (
	reference: NCNode | HTMLTextAreaElement,
	textToAdd: string
) => {
	if (globalThis.neuroCreate.graph) {
		if (textToAdd.length) {
			if ((reference as NCNode).uid) {
				// Reference is a node
				const referenceNode = reference as NCNode;
				const currentNode = globalThis.neuroCreate.graph.getNode(referenceNode.uid)!;
				currentNode.label = currentNode.label
					? `${currentNode.label}${textToAdd.substring(0, 1)}`
					: textToAdd.substring(0, 1);
				if (globalThis.neuroCreate.saveNodes) {
					globalThis.neuroCreate.saveNodes.update([currentNode]);
				}
				setTimeout(
					() => {
						addTextWithTypingEffect(referenceNode, textToAdd.substring(1, textToAdd.length));
					},
					20 + Math.random() * 80
				);
			} else {
				// Reference is a TextArea
				const referenceTextArea = reference as HTMLTextAreaElement;
				referenceTextArea.value = referenceTextArea.value
					? `${referenceTextArea.value}${textToAdd.substring(0, 1)}`
					: textToAdd.substring(0, 1);
				setTimeout(
					() => {
						addTextWithTypingEffect(referenceTextArea, textToAdd.substring(1, textToAdd.length));
					},
					20 + Math.random() * 80
				);
			}
		}
	}
};

export const onExitShowInstruction = (instruction: string) => {
	return {
		onexit: () => {
			showNonBlockingMessage(`&#x27A1;&#xFE0F; ${instruction}`);
		},
	};
};

export const openCollapsedMenuForTutorial = (
	tutorialTarget: HTMLElement,
	expandButton: HTMLButtonElement
) => {
	if (!tutorialTarget) {
		return;
	}
	const isMenuCollapsed = tutorialTarget.classList.contains('collapsed');
	const isMenuActive = expandButton.classList.contains('active');
	if (isMenuCollapsed && !isMenuActive) {
		expandButton.click();
		updatePosition();
		setTimeout(() => {
			updatePosition();
		}, 200);
	}
};

export const closeExpandedMenuAfterTutorial = (
	expandButton: HTMLButtonElement,
	collapseButton?: HTMLButtonElement
) => {
	// toggle expand menu if it is expanded
	if (window.innerWidth < WINDOW_BREAKPOINTS.laptop && expandButton.classList.contains('active')) {
		expandButton.click();
	}
	if (window.innerWidth >= WINDOW_BREAKPOINTS.laptop && collapseButton) {
		collapseButton.click();
	}
};
